import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { toast } from 'react-toastify';
import HeaderContentNew from '../../../components/shared/HeaderContentNew/HeaderContentNew';
import Loading from '../../../components/shared/Loading/Loading';
import { File as FileService } from '../../../services';
import EditRowModal from './edit-row-modal/EditRowModal';
import BulkPublishModal from './bulk-publish-modal/BulkPublishModal';
import TemplateDownloadModal from "./TemplateDownloadModal";
import { useBulkDirectories } from '../../../hooks/useBulkDirectories';
import { parseDate } from '../../../utils/bulkOrderHelpers';
import style from './BulkOrderCreatePage.module.scss';
import WrapperOrders from "../../../components/shared/WrapperContent";
import { useTranslation } from 'react-i18next';

// Строка валидируется в useBulkDirectories.validateRows (поля __valid / __errors)
const hasRowErrors = (row) => !row.__valid;

// Подсказка с ошибкой под ячейкой таблицы
const ErrorHint = ({ text }) =>
  text ? (
    <div className={cn(style.errorHint, style.errorHintRed)}>⚠ {text}</div>
  ) : null;

const formatFileSize = (bytes, t) => {
  if (bytes < 1024) return `${bytes} ${t('bulkCreate.fileSize.b')}`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ${t('bulkCreate.fileSize.kb')}`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} ${t('bulkCreate.fileSize.mb')}`;
};

const BulkOrderCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const directories = useBulkDirectories();

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  const handleSaveRow = (index, updatedRow) => {
    setUploadedData((prev) => prev.map((r, i) => (i === index ? updatedRow : r)));
  };

  const REQUIRED_ROW_IDS = ['countryIdFrom', 'cityIdFrom', 'countryIdTo', 'cityIdTo'];

  const handleProceed = () => {
    const hasErrors =
      uploadedData.length === 0 ||
      uploadedData.some(
        (row) => hasRowErrors(row) || REQUIRED_ROW_IDS.some((key) => !row[key]),
      );

    if (hasErrors) {
      toast.error(t('bulkCreate.toasts.tableErrors'));
      return;
    }

    setIsPublishModalOpen(true);
  };

  const handleFileSelect = (selected) => {
    if (selected) setFile(selected);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleFileInputChange = (e) => {
    const selected = e.target.files[0];
    if (selected) handleFileSelect(selected);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setIsUploading(true);
    try {
      const res = await FileService.uploadBulkOrders(formData);
      const result = res.data?.result;
      const rows = Array.isArray(result)
        ? result
        : Array.isArray(res.data)
          ? res.data
          : [];
      const validated = await directories.validateRows(rows);
      setUploadedData(validated);
      setStep(2);
      directories.loadCategories().catch(() => {});
    } catch (e) {
      console.error('Ошибка загрузки файла:', e);
      toast.error(t('bulkCreate.toasts.fileProcessError'));
    } finally {
      setIsUploading(false);
    }
  };

  const totalRows = uploadedData.length;
  const errorRows = uploadedData.filter(hasRowErrors);
  const readyRows = uploadedData.filter((r) => !hasRowErrors(r));

  const filteredData = uploadedData
    .filter((row) => {
      if (activeFilter === 'ready') return !hasRowErrors(row);
      if (activeFilter === 'errors') return hasRowErrors(row);
      return true;
    })
    .filter((row) => {
      if (!searchText) return true;
      const q = searchText.toLowerCase();
      return (
        (row.shippingDate || '').toLowerCase().includes(q) ||
        (row.categoryName || '').toLowerCase().includes(q) ||
        (row.category || '').toLowerCase().includes(q) ||
        (row.cityFrom || '').toLowerCase().includes(q) ||
        (row.cityTo || '').toLowerCase().includes(q) ||
        (row.addressFrom || '').toLowerCase().includes(q) ||
        (row.addressTo || '').toLowerCase().includes(q) ||
        (row.referenceNumber || '').toLowerCase().includes(q)
      );
    });

  return (
    <>
      <HeaderContentNew
        title={t('bulkCreate.title')}
        displayTitle
        displayCreateBtn={false}
        displayReloadBtn={false}
      />
      
      <div className={style.page}>
        {/* ── Step Indicator ── */}
        <div className={style.stepIndicator}>
          <div className={cn(style.stepItem, { [style.stepItemActive]: step === 1 })}>
            <div className={cn(style.stepCircle, { [style.stepCircleActive]: step === 1 })}>
              1
            </div>
            <div className={style.stepInfo}>
              <span className={cn(style.stepLabel, { [style.stepLabelActive]: step === 1 })}>
                {step === 1 ? t('bulkCreate.steps.step1Now') : t('bulkCreate.steps.step1')}
              </span>
              <span className={style.stepTitle}>{t('bulkCreate.steps.step1Title')}</span>
            </div>
          </div>

          <div className={cn(style.stepItem, { [style.stepItemActive]: step === 2 })}>
            <div className={cn(style.stepCircle, { [style.stepCircleActive]: step === 2 })}>
              2
            </div>
            <div className={style.stepInfo}>
              <span className={cn(style.stepLabel, { [style.stepLabelActive]: step === 2 })}>
                {step === 2 ? t('bulkCreate.steps.step2Now') : t('bulkCreate.steps.step2')}
              </span>
              <span className={style.stepTitle}>{t('bulkCreate.steps.step2Title')}</span>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        <div className={style.content}>
          {/* ── Step 1: Upload ── */}
          {step === 1 && (
            <div className={style.step1}>
              <div className={style.templateRow}>
                <div className={style.templateLabel}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#212121" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="#212121" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>
                    {t('bulkCreate.template.label')}{' '}
                    <span className={style.templateSub}>{t('bulkCreate.template.sub')}</span>
                  </span>
                </div>
                <button
                  type="button"
                  className={style.downloadLink}
                  onClick={() => setIsTemplateModalOpen(true)}
                >
                  {t('bulkCreate.template.download')}
                </button>
              </div>

              {/* Drop zone */}
              <div
                className={cn(style.dropZone, { [style.dropZoneDragOver]: isDragOver })}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className={style.dropZoneIconWrap}>
                  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                    <path d="M35 35s4-2 4-7c0-5-5-9-10-9a7 7 0 0 0-2 .2C25.5 15.5 22 13 18 13 12.5 13 8 17.5 8 23c0 .6.1 1.2.2 1.8C5 26 3 29 3 32.5 3 37.5 7 41.5 12 41.5H35z" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 41.5V26m-5.5 5.5L22 26l5.5 5.5" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className={style.dropZoneText}>{t('bulkCreate.dropzone.text')}</p>
                <p className={style.dropZoneHint}>{t('bulkCreate.dropzone.hint')}</p>
                <button
                  className={style.selectFileBtn}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  {t('bulkCreate.dropzone.selectFile')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
              </div>

              {/* File info */}
              {file && (
                <div className={style.fileInfo}>
                  <div className={style.fileIconWrap}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#1570EF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="14 2 14 8 20 8" stroke="#1570EF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className={style.fileDetails}>
                    <span className={style.fileName}>{file.name}</span>
                    <span className={style.fileMeta}>
                      {formatFileSize(file.size, t)} · {t('bulkCreate.fileInfo.uploaded')}
                    </span>
                  </div>
                  <button
                    className={style.fileActionBtn}
                    title={t('bulkCreate.fileInfo.replace')}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className={style.fileActionBtn}
                    title={t('bulkCreate.fileInfo.remove')}
                    onClick={() => setFile(null)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Review ── */}
          {step === 2 && (
            <div className={style.step2}>
              {/* Stats */}
              <div className={style.statsRow}>
                <div className={style.statCard}>
                  <span className={style.statValue} style={{ color: '#2F80ED' }}>
                    {totalRows}
                  </span>
                  <span className={style.statLabel}>{t('bulkCreate.stats.found')}</span>
                </div>
                <div className={style.statCard}>
                  <span className={style.statValue} style={{ color: '#27AE60' }}>
                    {readyRows.length}
                  </span>
                  <span className={style.statLabel}>{t('bulkCreate.stats.ready')}</span>
                </div>
                <div className={style.statCard}>
                  <span className={style.statValue} style={{ color: '#EB5757' }}>
                    {errorRows.length}
                  </span>
                  <span className={style.statLabel}>{t('bulkCreate.stats.errors')}</span>
                </div>
              </div>

              {/* Search & filters */}
              <div className={style.searchRow}>
                <div className={style.searchInput}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="#99A1AF" strokeWidth="1.5"/>
                    <path d="m21 21-4.35-4.35" stroke="#99A1AF" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <input
                    type="text"
                    placeholder={t('bulkCreate.search.placeholder')}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className={style.filterPills}>
                  <button
                    className={cn(style.pill, { [style.pillActive]: activeFilter === 'all' })}
                    onClick={() => setActiveFilter('all')}
                  >
                    <strong>{totalRows}</strong>&nbsp;{t('bulkCreate.filters.all')}
                  </button>
                  <button
                    className={cn(style.pill, { [style.pillActive]: activeFilter === 'ready' })}
                    onClick={() => setActiveFilter('ready')}
                  >
                    <strong>{readyRows.length}</strong>&nbsp;{t('bulkCreate.filters.ready')}
                  </button>
                  <button
                    className={cn(style.pill, { [style.pillActive]: activeFilter === 'errors' })}
                    onClick={() => setActiveFilter('errors')}
                  >
                    <strong>{errorRows.length}</strong>&nbsp;{t('bulkCreate.filters.errors')}
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className={style.tableContainer}>
                <div className={style.tableWrapper}>
                  <table className={style.table}>
                    <thead>
                      <tr>
                        <th>{t('bulkCreate.table.shippingDate')}</th>
                        <th>{t('bulkCreate.table.category')}</th>
                        <th className={style.colRight}>{t('bulkCreate.table.weight')}</th>
                        <th>{t('bulkCreate.table.addressFrom')}</th>
                        <th>{t('bulkCreate.table.countryFrom')}</th>
                        <th>{t('bulkCreate.table.cityFrom')}</th>
                        <th>{t('bulkCreate.table.addressTo')}</th>
                        <th>{t('bulkCreate.table.countryTo')}</th>
                        <th>{t('bulkCreate.table.cityTo')}</th>
                        <th>{t('bulkCreate.table.vehicleType')}</th>
                        <th>{t('bulkCreate.table.temperature')}</th>
                        <th>{t('bulkCreate.table.tnved')}</th>
                        <th>{t('bulkCreate.fields.referenceNumber')}</th>
                        <th>{t('bulkCreate.table.bidCurrency')}</th>
                        <th>{t('bulkCreate.table.sender')}</th>
                        <th>{t('bulkCreate.table.senderPhone')}</th>
                        <th>{t('bulkCreate.table.recipient')}</th>
                        <th>{t('bulkCreate.table.recipientPhone')}</th>
                        <th>{t('bulkCreate.table.tenderEnd')}</th>
                        <th className={style.actionHeader}>{t('bulkCreate.table.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((row, idx) => {
                        const hasError = hasRowErrors(row);
                        return (
                          <tr
                            key={idx}
                            className={cn({ [style.rowError]: hasError })}
                          >
                            <td>
                              {parseDate(row.shippingDate)
                                ?.toLocaleDateString('ru-RU') || '—'}
                              <ErrorHint text={row.__errors?.shippingDate} />
                            </td>
                            <td>
                              {row.categoryName || row.category || (
                                <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              )}
                              <ErrorHint text={row.__errors?.category} />
                            </td>
                            <td className={style.colRight}>
                              {row.weight ?? '—'}
                              <ErrorHint text={row.__errors?.weight} />
                            </td>
                            <td>
                              <div className={style.tdOverflow}>{row.addressFrom || '—'}</div>
                              <ErrorHint text={row.__errors?.addressFrom} />
                            </td>
                            <td>
                              {row.countryFromName || (
                                <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              )}
                              <ErrorHint text={row.__errors?.countryFrom} />
                            </td>
                            <td>
                              {row.cityFrom || (
                                <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              )}
                              <ErrorHint text={row.__errors?.cityFrom} />
                            </td>
                            <td>
                              <div className={style.tdOverflow}>
                                {row.addressTo || (
                                  <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                                )}
                              </div>
                              <ErrorHint text={row.__errors?.addressTo} />
                            </td>
                            <td>
                              {row.countryToName || (
                                <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              )}
                              <ErrorHint text={row.__errors?.countryTo} />
                            </td>
                            <td>
                              {row.cityTo || (
                                <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              )}
                              <ErrorHint text={row.__errors?.cityTo} />
                            </td>
                            <td>
                              {row.vehicleType || row.vehicleTypeName || '—'}
                              <ErrorHint text={row.__errors?.type} />
                            </td>
                            <td>
                              {row.temperature || '—'}
                              <ErrorHint text={row.__errors?.temperature} />
                            </td>
                            <td>{row.tnvedCode || '—'}</td>
                            <td>{row.referenceNumber || '—'}</td>
                            <td>
                              {row.currency || t('analytics.allCurrencies')}
                              <ErrorHint text={row.__errors?.currency} />
                            </td>
                            <td>{row.senderName || '—'}</td>
                            <td>{row.senderPhoneNumber || '—'}</td>
                            <td>{row.recipientName || '—'}</td>
                            <td>{row.recipientPhoneNumber || '—'}</td>
                            <td>
                              {row.tenderDateEnd
                                ? new Date(row.tenderDateEnd).toLocaleString('ru-RU', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : <span className={style.emptyCell}>{t('bulkCreate.cells.notSpecified')}</span>
                              }
                              <ErrorHint text={row.__errors?.tenderDateEnd} />
                            </td>
                            <td className={style.actionCell}>
                              <button
                                className={style.actionBtn}
                                onClick={() => setEditingRowIndex(uploadedData.indexOf(row))}
                              >
                                {hasError ? t('bulkCreate.cells.fix') : t('bulkCreate.cells.open')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Bottom Bar ── */}
        <div className={style.bottomBar}>
          <div />
          <div className={style.bottomActions}>
            {step === 1 ? (
              <>
                <button className={style.btnOutline} onClick={() => navigate('/main')}>
                  {t('bulkCreate.buttons.cancel')}
                </button>
                <button
                  className={style.btnPrimary}
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                >
                  {isUploading ? t('bulkCreate.buttons.uploading') : t('bulkCreate.buttons.next')}
                </button>
              </>
            ) : (
              <>
                <button className={style.btnOutline} onClick={() => setStep(1)}>
                  {t('bulkCreate.buttons.back')}
                </button>
                <button
                  className={style.btnPrimary}
                  onClick={handleProceed}
                >
                  {t('bulkCreate.buttons.next')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Uploading overlay ── */}
        {isUploading && (
          <div className={style.uploadingOverlay}>
            <div className={style.uploadingContent}>
              <Loading inBlock />
              <p className={style.uploadingText}>
                {t('bulkCreate.uploadingOverlay')}
              </p>
            </div>
          </div>
        )}

        {/* ── Edit Row Modal ── */}
        {editingRowIndex !== null && uploadedData[editingRowIndex] && (
          <EditRowModal
            row={uploadedData[editingRowIndex]}
            rowIndex={editingRowIndex}
            directories={directories}
            onClose={() => setEditingRowIndex(null)}
            onSave={handleSaveRow}
          />
        )}
      </div>

      <TemplateDownloadModal isOpen={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)} />

      {/* ── Publish Modal ── */}
      {isPublishModalOpen && (
        <BulkPublishModal
          rows={readyRows}
          onClose={() => setIsPublishModalOpen(false)}
        />
      )}
    </>
  );
};

export default BulkOrderCreatePage;
